import React, { useState } from "react";
import { Table, Button, Space, Tooltip, Tag, Badge, Switch } from "antd";
import {
  FaRegUserCircle,
  FaEdit,
  FaTrash,
  FaEye,
  FaShieldAlt,
  FaSignOutAlt,
  FaBan,
  FaUnlock
} from "react-icons/fa";
import { useCan } from "../../../utils/permissions";
import { globalLogout } from "../../../functions/auth";
import { toast } from "react-toastify";
import LogoutUserModal from "./LogoutUserModal";
import SuspendUserModal from "./SuspendUserModal";

const UserTable = ({
  filteredUsers,
  loading,
  pageC,
  setPageC,
  totalUsers,
  setSelectedUser,
  setIsEditModalVisible,
  setIsViewModalVisible,
  setIs2FAModalVisible,
  setIsDeleteModalVisible,
  handleUserDetailsClick,
  editForm,
  handleSuspendUser,
  limit,
  setLimit
}) => {
  const can = useCan();
  const canRead = can("user", "read");
  const canUpdate = can("user", "update");
  const canDelete = can("user", "delete");
  const canManage2FA = can("2fa", "update") || can("2fa", "approve");
  const canLogout = can("globalLogout", "delete");
  const canSuspend = can("suspend", "update");
  const [logoutTarget, setLogoutTarget] = useState(null);
  const [suspendTarget, setSuspendTarget] = useState(null);
  const [suspendBlock, setSuspendBlock] = useState(true);
  const columns = [
    {
      title: "User",
      key: "user",
      render: (_, record) => (
        <Space>
          <FaRegUserCircle size={20} />
          <div>
            <div style={{ fontWeight: 'bold' }}>{record.name}</div>
            <div style={{ fontSize: '12px', color: '#666' }}>{record.email}</div>
          </div>
        </Space>
      ),
    },
    {
      title: "Tech ID",
      dataIndex: "techId",
      key: "techId",
      render: (text) => <Tag color="blue">{text}</Tag>,
    },
    {
      title: "Department",
      dataIndex: "department",
      key: "department",
      render: (text) => text || "N/A",
    },
    {
      title: "Role",
      dataIndex: "roleName",
      key: "roleName",
      render: (text) => (
        <Tag color={text === "admin" ? "red" : text === "tech" ? "blue" : "green"}>
          {text }
        </Tag>
      ),
    },
    {
      title: "Login Access",
      dataIndex: "loginAccess",
      key: "loginAccess",
      render: (accessArray = []) => (
        <>
          {accessArray.map((access) => {
            let color = "blue";
            if (access.toLowerCase() === "mobile") color = "purple";
            else if (access.toLowerCase() === "both") color = "green";
    
            return (
              <Tag key={access} color={color}>
                {access.charAt(0).toUpperCase() + access.slice(1)}
              </Tag>
            );
          })}
        </>
      ),
    },    
    {
      title: "2FA Status",
      key: "twoFactorEnabled",
      render: (_, record) => (
        <Badge
          status={record.twoFactorEnabled ? "success" : "default"}
          text={record.twoFactorEnabled ? "Enabled" : "Disabled"}
        />
      ),
    },
    {
      title: "Verification",
      key: "IsVerified",
      render: (_, record) => (
        <Tag color={record.IsVerified ? "green" : "red"}>
          {record.IsVerified ? "Verified" : "Unverified"}
        </Tag>
      ),
    },
    {
      title: "Status",
      key: "status",
      render: (_, record) => {
        let color = "green";
        let text = "Active";
    
        if (record.isDeleted) {
          color = "red";
          text = "Deleted";
        } else if (record.isBlocked) {
          color = "orange";
          text = "Suspended";
        }
    
        return <Tag color={color}>{text}</Tag>;
      },
    },    
    {
      title: "Next Orders",
      key: "nextOrders",
      render: (_, record) => (
        <div>
          {record.nextOrders && record.nextOrders.length > 0 ? (
            <Tag color="blue">#{record.nextOrders[0].tranId}</Tag>
          ) : (
            <Tag color="default">No Orders</Tag>
          )}
        </div>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Tooltip title="View Details">
            <Button
              icon={<FaEye />}
              onClick={() => canRead && handleUserDetailsClick(record._id)}
              disabled={!canRead}
            />
          </Tooltip>
          <Tooltip title="Edit User">
            <Button
              icon={<FaEdit />}
              type="primary"
              onClick={() => {
                if (!canUpdate) return;
                if (record.isDeleted) {
                  toast.info(`User ${record.name} is deleted. Please restore it first.`);
                  return;
                }
                setSelectedUser(record);
                editForm.setFieldsValue(record);
                setIsEditModalVisible(true);
              }}
              disabled={!canUpdate}
            />
          </Tooltip>
          <Tooltip title="2FA Management">
            <Button
              icon={<FaShieldAlt />}
              onClick={() => {
                if (!canManage2FA) return;
                if (record.isDeleted) {
                  toast.info(`User ${record.name} is deleted. Please restore it first.`);
                  return;
                }
                setSelectedUser(record);
                setIs2FAModalVisible(true);
              }}
              disabled={!canManage2FA}
            />
          </Tooltip>
          <Tooltip title={record.isDeleted ? "Restore User" : "Delete User"}>
            <Button
              icon={record.isDeleted ? <FaUnlock /> : <FaTrash />}
              danger={!record.isDeleted}
              type={record.isDeleted ? "primary" : "default"}
              onClick={() => {
                if (!canDelete) return;
                setSelectedUser(record);
                setIsDeleteModalVisible(true);
              }}
              disabled={!canDelete}
            />
          </Tooltip>

          <Tooltip title="Logout">
            <Button
              icon={<FaSignOutAlt />}
              
              onClick={async () => {
                if (!canDelete) return;
                if (record.isDeleted) {
                  toast.info(`User ${record.name} is deleted. Please restore it first.`);
                  return;
                }
                setLogoutTarget(record);
              }}
              disabled={!canLogout}
            />
          </Tooltip>
          <Tooltip title={record.isBlocked ? "Unsuspend User" : "Suspend User"}>
            <Switch
              checked={!record.isBlocked}
              onChange={(checked) => {
                if (!canSuspend) return;
                if (record.isDeleted) {
                  toast.info(`User ${record.name} is deleted. Please restore it first.`);
                  return;
                }
                const nextBlock = !checked;
                setSuspendBlock(nextBlock);
                setSuspendTarget(record);
              }}
              disabled={!canSuspend}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <>
    <Table
      columns={columns}
      dataSource={filteredUsers}
      rowKey="_id"
      pagination={{
        current: pageC,
        total: totalUsers,
        pageSize: typeof limit === 'number' ? limit : 10,
        onChange: (page, pageSize) => {
          if (typeof setLimit === 'function' && typeof pageSize === 'number') {
            setLimit(pageSize);
          }
          setPageC(page);
        },
        onShowSizeChange: (current, pageSize) => {
          if (typeof setLimit === 'function') {
            setLimit(pageSize);
          }
          setPageC(1);
        },
        showSizeChanger: true,
        showQuickJumper: true,
        showTotal: (total, range) =>
          `${range[0]}-${range[1]} of ${total} users`,
      }}
      bordered
      size="middle"
      scroll={{ x: 1000 }}
      loading={loading}
      locale={{
        emptyText: filteredUsers.length === 0
          ? "No users found"
          : "No users match your search criteria"
      }}
    />
    <LogoutUserModal
      open={!!logoutTarget}
      user={logoutTarget}
      onCancel={() => setLogoutTarget(null)}
      onConfirm={async () => {
        if (!logoutTarget) return;
        try {
          await globalLogout(logoutTarget._id);
          toast.success("User logged out from all devices");
          setLogoutTarget(null);
        } catch (err) {
          toast.error(err?.response?.data?.message || "Failed to logout user globally");
        }
      }}
    />
    <SuspendUserModal
      open={!!suspendTarget}
      user={suspendTarget}
      block={suspendBlock}
      onCancel={() => {
        setSuspendTarget(null);
      }}
      onConfirm={async () => {
        if (!suspendTarget) return;
        try {
          if (typeof handleSuspendUser === 'function') {
            await handleSuspendUser(suspendTarget, suspendBlock);
            setSuspendTarget(null);
          } else {
            throw new Error('Suspend handler not available');
          }
        } catch (err) {
          toast.error(err?.response?.data?.message || err?.message || "Failed to update suspension");
        }
      }}
    />
    </>
  );
};

export default UserTable;
